/**
 * EventsComingSoon — premium "coming soon" overlay shown on the Events page
 * while there are no upcoming events scheduled.
 *
 * Visual language mirrors ShopComingSoon / BookingComingSoon:
 *   • Blurred decorative event-card silhouettes in the background
 *   • Glassmorphism overlay card with gold accent + animated lock icon
 *   • Backdrop blur, soft radial glow, smooth fade-in
 *
 * Renders inside the existing Events page layout — the navbar / sidebar /
 * filters bar remain fully interactive; only the content area is locked.
 */
import { motion } from 'framer-motion';
import { Lock, Calendar, MapPin, Users } from 'lucide-react';

const PLACEHOLDER_CARDS = [
  { day: '14', month: 'MAR', tier: 'Elite', title: 'Carp Cast Founders Social' },
  { day: '22', month: 'MAR', tier: 'Pro',   title: 'Spring Open Match — Linear' },
  { day: '05', month: 'APR', tier: 'Basic', title: 'Beginners\' Bankside Meetup' },
  { day: '19', month: 'APR', tier: 'Pro',   title: 'UK Big-Carp Weekender' },
];

const BlurredEventCard = ({ card, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 0.55, y: 0 }}
    transition={{ delay: index * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    aria-hidden="true"
    className="glass-card overflow-hidden blur-[3px] select-none pointer-events-none"
  >
    <div className="flex flex-col md:flex-row">
      <div className="md:w-32 bg-[#141714] p-4 flex md:flex-col items-center justify-center text-center border-b md:border-b-0 md:border-r border-white/5">
        <p className="font-['Barlow_Condensed'] text-4xl font-bold text-[#c8b273]">{card.day}</p>
        <p className="text-white uppercase tracking-wider ml-2 md:ml-0">{card.month}</p>
      </div>
      <div className="flex-1 p-4 md:p-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#2d352d] text-[#c8b273]/70">
            {card.tier}
          </span>
        </div>
        <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white/70">
          {card.title}
        </h3>
        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-[#8a948a]/70">
          <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />UK Fishery</span>
          <span className="flex items-center gap-1"><Users className="w-4 h-4" />Limited spots</span>
        </div>
      </div>
    </div>
  </motion.div>
);

const EventsComingSoon = () => {
  return (
    <div
      className="relative"
      data-testid="events-coming-soon"
    >
      {/* Blurred decorative event cards behind the overlay */}
      <div className="space-y-4 pointer-events-none" aria-hidden="true">
        {PLACEHOLDER_CARDS.map((c, i) => (
          <BlurredEventCard key={i} card={c} index={i} />
        ))}
      </div>

      {/* Darkening + backdrop-blur layer */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#050605]/40 via-[#050605]/70 to-[#050605]/95 backdrop-blur-sm" />

      {/* Centered glassmorphism overlay card */}
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
        className="absolute inset-0 flex items-center justify-center px-4 py-12"
        data-testid="events-coming-soon-overlay"
      >
        <div
          className="relative w-full max-w-md rounded-2xl border border-[#c8b273]/25 bg-[#0b0d0b]/90 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(200,178,115,0.25)] px-6 py-10 md:px-10 md:py-12 text-center overflow-hidden"
        >
          {/* Soft radial glow behind content */}
          <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-gradient-to-b from-[#c8b273]/20 to-transparent blur-3xl" />

          {/* Lock icon with animated pulsing glow */}
          <div className="relative inline-flex items-center justify-center mb-6">
            {/* Outer pulse ring 1 */}
            <span
              className="absolute inline-flex h-20 w-20 rounded-full bg-[#c8b273]/15 animate-ping"
              aria-hidden="true"
              style={{ animationDuration: '2.4s' }}
            />
            {/* Outer pulse ring 2 (slightly offset) */}
            <span
              className="absolute inline-flex h-16 w-16 rounded-full bg-[#c8b273]/25 animate-pulse"
              aria-hidden="true"
              style={{ animationDuration: '2s' }}
            />
            {/* Lock icon container */}
            <span
              className="relative inline-flex h-16 w-16 items-center justify-center rounded-full border border-[#c8b273]/50 bg-[#c8b273]/10 shadow-[0_0_30px_-5px_rgba(200,178,115,0.45)]"
              data-testid="events-coming-soon-lock"
            >
              <Lock className="w-6 h-6 text-[#c8b273]" strokeWidth={2.2} />
            </span>
          </div>

          {/* Heading */}
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#c8b273] mb-3">
            <Calendar className="inline-block w-3 h-3 -mt-0.5 mr-1.5" />
            Events Calendar
          </p>
          <h2
            className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white mb-4"
            data-testid="events-coming-soon-heading"
          >
            Coming Soon
          </h2>

          {/* Subtext */}
          <p className="text-sm md:text-base text-[#a8b0a8] leading-relaxed max-w-sm mx-auto">
            We&apos;re building something special.
            <br />
            Upcoming Carp Cast events, socials, competitions and lake meetups will be available soon.
          </p>

          {/* Decorative divider */}
          <div className="mt-8 mx-auto h-px w-24 bg-gradient-to-r from-transparent via-[#c8b273]/40 to-transparent" />

          <p className="text-[10px] uppercase tracking-[0.25em] text-[#6b7d6b] mt-6">
            You&apos;ll be the first to know when it goes live
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default EventsComingSoon;
